import React from 'react';
import { Link } from 'react-router-dom';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaUsers as FaUsersBase, 
  FaBullseye as FaBullseyeBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaUsers = FaUsersBase as React.ElementType;
const FaBullseye = FaBullseyeBase as React.ElementType;

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, ArcElement);

const CustomerSegmentation: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const scatterData = {
    datasets: [
      {
        label: 'High Value (Champions)',
        data: Array.from({ length: 20 }, () => ({ x: Math.random() * 20 + 80, y: Math.random() * 20 + 80 })),
        backgroundColor: '#10b981',
      },
      {
        label: 'At Risk',
        data: Array.from({ length: 20 }, () => ({ x: Math.random() * 30 + 20, y: Math.random() * 30 + 50 })),
        backgroundColor: '#f59e0b',
      },
      {
        label: 'Lost',
        data: Array.from({ length: 20 }, () => ({ x: Math.random() * 30, y: Math.random() * 30 })),
        backgroundColor: '#ef4444',
      },
    ],
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl font-bold text-primary mb-4">Customer Segmentation Engine</h1>
          <p className="text-xl text-secondary max-w-3xl">
            Unlocking marketing ROI by grouping customers based on purchasing behavior using Unsupervised Machine Learning.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Main Chart */}
          <div className="lg:col-span-2 bg-primary p-6 rounded-xl shadow-lg border border-primary">
            <h3 className="text-lg font-bold text-primary mb-4">Cluster Visualization (Frequency vs. Monetary)</h3>
            <div className="h-80">
              <Scatter 
                data={scatterData} 
                options={{ 
                  responsive: true, 
                  maintainAspectRatio: false,
                  scales: {
                    x: { title: { display: true, text: 'Frequency Score' }, grid: { color: isDark ? '#374151' : '#e5e7eb' } },
                    y: { title: { display: true, text: 'Monetary Score' }, grid: { color: isDark ? '#374151' : '#e5e7eb' } }
                  }
                }} 
              />
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-6">
            <div className="bg-primary p-6 rounded-xl border border-primary">
              <div className="flex items-center gap-3 mb-2">
                <FaUsers className="text-accent" size={24} />
                <h4 className="font-bold text-primary">Total Analyzed</h4>
              </div>
              <p className="text-3xl font-bold text-primary">14,502</p>
              <p className="text-sm text-secondary">Unique Customer IDs</p>
            </div>
            
            <div className="bg-primary p-6 rounded-xl border border-primary">
              <div className="flex items-center gap-3 mb-2">
                <FaBullseye className="text-green-500" size={24} />
                <h4 className="font-bold text-primary">Silhouette Score</h4>
              </div>
              <p className="text-3xl font-bold text-primary">0.68</p>
              <p className="text-sm text-secondary">Cluster Separation Quality</p>
            </div>
          </div>
        </div>

        <div className="bg-primary p-8 rounded-xl border border-primary">
          <h2 className="text-2xl font-bold text-primary mb-6">Methodology: RFM Analysis + K-Means</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold text-accent mb-2">Recency (R)</h4>
              <p className="text-secondary text-sm">How recently did the customer purchase? Lower values indicate higher engagement probability.</p>
            </div>
            <div>
              <h4 className="font-bold text-accent mb-2">Frequency (F)</h4>
              <p className="text-secondary text-sm">How often do they purchase? High frequency indicates loyalty and product stickiness.</p>
            </div>
            <div>
              <h4 className="font-bold text-accent mb-2">Monetary (M)</h4>
              <p className="text-secondary text-sm">How much do they spend? Identifies "Whales" vs low-value transactional users.</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-primary">
            <p className="text-secondary">
              <strong>Implementation Details:</strong> Data is preprocessed using Python (StandardScaler) to normalize the variance. 
              The Elbow Method was used to determine the optimal number of clusters (k=4). The results are exported to a PostgreSQL database for the frontend dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSegmentation;